import { h } from 'preact';
import { openModal } from '../theme/modal';
import { Content as WishlistContent } from '../wishlist/content';
import { addToWishlist, createList, isInSelectedWishlist, refreshLists, removeFromWishlist } from '../wishlist';
const BUTTON_SELECTOR = '.js-wishlist-button';
const VARIANT_CHANGED_EVENT = 'product:wishlist-variant-changed';
/*
 * Fired (cancelable, bubbling) on the clicked toggle when no variant is
 * selected yet. A container that can ask for one itself - the Gift Sets PDP
 * mobile bar opens its size sheet - cancels it; otherwise the "select a
 * size" modal opens.
 */ export const WISHLIST_SELECT_VARIANT_EVENT = 'product:wishlist-select-variant';
const ADD_VARIANT_EVENT = 'product:wishlist-add-variant';
/*
 * A page can hold more than one toggle for the same product, so every
 * instance is bound, and they all share the state below.
 */ const getButtons = ()=>Array.from(document.querySelectorAll(BUTTON_SELECTOR)).filter((button)=>button instanceof HTMLButtonElement);
const parseProduct = (button)=>{
    const productJson = button.dataset.productJson;
    if (!productJson) {
        return null;
    }
    try {
        return JSON.parse(productJson);
    } catch (error) {
        console.error('Error parsing wishlist product JSON:', error);
        return null;
    }
};
const getVariantTitle = (variant)=>{
    return variant === null || variant === void 0 ? void 0 : variant.title;
};
const getVariantById = (product, variantId)=>{
    if (variantId == null) {
        return null;
    }
    const parsedVariantId = Number(variantId);
    if (!Number.isFinite(parsedVariantId)) {
        return null;
    }
    return product.variants.find((variant)=>variant.id === parsedVariantId) || null;
};
const getSwymProduct = (product, selectedVariant)=>({
        epi: selectedVariant.id,
        empi: product.id,
        du: window.location.href,
        cprops: {
            images: product.images
        }
    });
const setLoadingState = (button, isLoading)=>{
    button.setAttribute('aria-busy', String(isLoading));
    button.disabled = isLoading;
    button.classList.toggle('button__wishlist--disabled', isLoading);
};
// Shown only until the first Swym response arrives, so the button doesn't
// flash from "not in wishlist" to "in wishlist" once the real data lands.
const setPendingState = (button, isPending)=>{
    button.setAttribute('aria-busy', String(isPending));
    button.disabled = isPending;
    button.classList.toggle('Product-wishlist-button--pending', isPending);
};
const setWishlistState = (button, isActive)=>{
    var _button_querySelector;
    button.dataset.wishlistButton = String(isActive);
    button.setAttribute('aria-label', isActive ? 'Added to wishlist' : 'Add to wishlist');
    (_button_querySelector = button.querySelector('svg')) === null || _button_querySelector === void 0 ? void 0 : _button_querySelector.classList.toggle('wishlist__icon--active', isActive);
};
const openWishlistModal = ({ product , selectedVariant , isActive , action , wishlistName  })=>{
    openModal(h(WishlistContent, {
        targetEl: null,
        selectedVariantId: (selectedVariant === null || selectedVariant === void 0 ? void 0 : selectedVariant.id) || null,
        productId: product.id,
        productUrl: window.location.href,
        variantTitle: getVariantTitle(selectedVariant),
        productTitle: product.title,
        image: (product === null || product === void 0 ? void 0 : product.featured_image) || null,
        size: 'small',
        isInWishlist: isActive,
        action,
        wishlists: window.swymWishLists || [],
        product,
        wishlistName
    }), {
        ariaLabel: 'Wishlist form and details',
        id: 'wishlist-modal',
        category: 'wishlist'
    });
};
const openSelectSizeModal = (variantString)=>{
    openModal(h('p', {
        className: 'wishlist-select-size-modal__message',
        'data-testid': 'wishlist-select-size-modal'
    }, `Please select a ${variantString.toLowerCase()} to add to wishlist`), {
        ariaLabel: `Select a ${variantString.toLowerCase()} to add to wishlist`,
        id: 'wishlist-select-size-modal',
        category: 'wishlist'
    });
};
const ensureWishlist = async ()=>{
    const swat = window._swat;
    if (!swat) {
        throw new Error('Swym API not loaded');
    }
    if (window.swymSelectedListId === undefined) {
        const list = await createList(swat, undefined, true);
        window.swymSelectedListId = list.lid;
        window.swymSelectedListName = list.lname;
    }
    return swat;
};
export const init = async ()=>{
    const buttons = getButtons();
    if (buttons.length === 0) {
        return;
    }
    const productSource = buttons.find((button)=>parseProduct(button) !== null);
    const parsedProduct = productSource ? parseProduct(productSource) : null;
    if (!parsedProduct || !productSource) {
        return;
    }
    let product = parsedProduct;
    let selectedVariant = getVariantById(product, productSource.dataset.variantId);
    let firstOptionWithoutSelectedValue = null;
    let actionWishlistLoading = false;
    let hasReceivedWishlistData = false;
    const updateWishlistStatus = ()=>{
        const isActive = selectedVariant ? isInSelectedWishlist(selectedVariant.id, product.id) : false;
        buttons.forEach((button)=>setWishlistState(button, isActive));
    };
    const handleVariantChanged = (event)=>{
        const detail = event.detail;
        product = detail.product;
        selectedVariant = detail.selectedVariant;
        firstOptionWithoutSelectedValue = detail.firstOptionWithoutSelectedValue;
        if (selectedVariant) {
            const variantId = String(selectedVariant.id);
            buttons.forEach((button)=>button.dataset.variantId = variantId);
        }
        updateWishlistStatus();
    };
    /*
   * Adds the variant, or removes it when it is already in the selected list.
   * With `addOnly`, an already wishlisted variant just shows the modal, where
   * the customer can still manage their lists.
   */ const toggleVariant = async (activeVariant, addOnly = false)=>{
        const swymProduct = getSwymProduct(product, activeVariant);
        const isCurrentlyInSelectedWishlist = isInSelectedWishlist(activeVariant.id, product.id);
        if (addOnly && isCurrentlyInSelectedWishlist) {
            openWishlistModal({
                product,
                selectedVariant: activeVariant,
                isActive: true,
                action: 'added',
                wishlistName: window.swymSelectedListName
            });
            return;
        }
        actionWishlistLoading = true;
        buttons.forEach((button)=>setLoadingState(button, true));
        try {
            const swat = await ensureWishlist();
            const removedFromListName = window.swymSelectedListName;
            if (isCurrentlyInSelectedWishlist) {
                if (window.swymSelectedListId) {
                    await removeFromWishlist(window.swymSelectedListId, swat, swymProduct);
                }
                await refreshLists(swat);
                updateWishlistStatus();
                // The item may still live in other lists: let the user see and
                // manage those instead of just showing a plain "removed" message.
                openWishlistModal({
                    product,
                    selectedVariant: activeVariant,
                    isActive: false,
                    action: 'removed',
                    wishlistName: removedFromListName
                });
            } else {
                await addToWishlist(window.swymSelectedListId, swat, swymProduct);
                await refreshLists(swat);
                buttons.forEach((button)=>setWishlistState(button, true));
                openWishlistModal({
                    product,
                    selectedVariant: activeVariant,
                    isActive: true,
                    action: 'added',
                    wishlistName: window.swymSelectedListName
                });
            }
        } catch (error) {
            console.error(error);
            updateWishlistStatus();
        } finally{
            actionWishlistLoading = false;
            buttons.forEach((button)=>setLoadingState(button, false));
        }
    };
    const handleClick = async (clickedButton)=>{
        if (actionWishlistLoading) {
            return;
        }
        const activeVariant = selectedVariant || getVariantById(product, clickedButton.dataset.variantId);
        if (!activeVariant) {
            console.error('No variant selected');
            return;
        }
        if (firstOptionWithoutSelectedValue !== null) {
            const handledByContainer = !clickedButton.dispatchEvent(new CustomEvent(WISHLIST_SELECT_VARIANT_EVENT, {
                bubbles: true,
                cancelable: true
            }));
            if (!handledByContainer) {
                openSelectSizeModal(firstOptionWithoutSelectedValue || 'size');
            }
            return;
        }
        await toggleVariant(activeVariant);
    };
    /*
   * A variant picked in answer to WISHLIST_SELECT_VARIANT_EVENT. It becomes
   * the one the toggles reflect, but the size still counts as unselected, so
   * the next click asks again rather than removing it.
   */ const handleAddVariant = async (event)=>{
        if (actionWishlistLoading) {
            return;
        }
        const { variantId  } = event.detail;
        const variant = getVariantById(product, variantId);
        if (!variant) {
            return;
        }
        selectedVariant = variant;
        buttons.forEach((button)=>button.dataset.variantId = String(variantId));
        updateWishlistStatus();
        await toggleVariant(variant, true);
    };
    // The button ships disabled/pending in the HTML (we don't know yet
    // whether this variant is wishlisted); clear that once the first Swym
    // response arrives so it doesn't flash an incorrect "not in wishlist"
    // state in the meantime.
    window.addEventListener(VARIANT_CHANGED_EVENT, handleVariantChanged);
    window.addEventListener(ADD_VARIANT_EVENT, handleAddVariant);
    window.addEventListener('swym:ready', ()=>{
        if (!hasReceivedWishlistData) {
            hasReceivedWishlistData = true;
            buttons.forEach((button)=>setPendingState(button, false));
        }
        updateWishlistStatus();
    });
    buttons.forEach((button)=>{
        button.addEventListener('click', ()=>handleClick(button));
    });
};
export const dispatchWishlistVariantChanged = (detail)=>{
    window.dispatchEvent(new CustomEvent(VARIANT_CHANGED_EVENT, {
        detail
    }));
};
export const addVariantToWishlist = (variantId)=>{
    window.dispatchEvent(new CustomEvent(ADD_VARIANT_EVENT, {
        detail: {
            variantId
        }
    }));
};
